// src/data/roleplay.js
// Data layer for the SDR Roleplay tool.
//  • Live turns + scoring go through Cloud Functions (the Anthropic key stays
//    server-side) — see roleplayTurn() / scoreSession() below.
//  • Personas, config, sessions, and objections are read/written in Firestore
//    with the same subscribe-style listeners the rest of the Hub uses.

import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db, callFunction } from '../firebase';
import {
  DEFAULT_DIFFICULTY_CONFIG,
  DEFAULT_RUBRIC,
  DEFAULT_MODEL,
  DEFAULT_LIMITS,
  STARTER_PERSONAS,
} from '../config/roleplay';

const PERSONAS = 'roleplay_personas';
const CONFIG = 'roleplay_config';
const SESSIONS = 'roleplay_sessions';
const OBJECTIONS = 'roleplay_objections';

// ── Cloud Function calls ──────────────────────────────────────────────────

// Get the prospect's next line. `history` is [{speaker:'rep'|'prospect', text}].
export function roleplayTurn({ personaId, difficulty, history, repMessage }) {
  return callFunction('roleplayTurn', { personaId, difficulty, history, repMessage });
}

// Score + persist a completed call. `turnUsage` is the accumulated token usage
// from the live turns (for cost display). Returns
// { id, scores, overall, summary, coachingTip, model, usage, costUsd }.
export function scoreSession({ personaId, difficulty, track, transcript, repName, turnUsage }) {
  return callFunction('scoreSession', {
    personaId,
    difficulty,
    track,
    transcript,
    repName,
    turnUsage,
  });
}

// ── Personas ──────────────────────────────────────────────────────────────

export function subscribePersonas(cb, onError) {
  return onSnapshot(
    collection(db, PERSONAS),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error('subscribePersonas failed:', err);
      if (onError) onError(err);
    }
  );
}

// Create/replace a persona. `id` is the kebab-case slug used as the doc id.
export async function savePersona(id, data, addedBy) {
  await setDoc(
    doc(db, PERSONAS, id),
    {
      title: data.title || '',
      company: data.company || '',
      track: data.track || 'cold-open',
      brief: data.brief || '',
      knownObjections: Array.isArray(data.knownObjections) ? data.knownObjections : [],
      difficultyOverrides: data.difficultyOverrides || {},
      archived: !!data.archived,
      updatedBy: (addedBy || '').toLowerCase(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

// Soft-delete: personas with logged sessions must not be hard-deleted (breaks
// history joins). Callers use this instead of a real delete.
export async function archivePersona(id) {
  await updateDoc(doc(db, PERSONAS, id), { archived: true, updatedAt: serverTimestamp() });
}

export async function deletePersona(id) {
  await deleteDoc(doc(db, PERSONAS, id));
}

// Append an approved objection line to a persona's knownObjections.
export async function addKnownObjection(personaId, text) {
  const snap = await getDocs(
    query(collection(db, PERSONAS), where('__name__', '==', personaId))
  );
  const existing = snap.docs[0]?.data()?.knownObjections || [];
  if (existing.includes(text)) return;
  await updateDoc(doc(db, PERSONAS, personaId), {
    knownObjections: [...existing, text],
    updatedAt: serverTimestamp(),
  });
}

// ── Config (difficulty / rubric / guardrails / tracks) ─────────────────────

export function subscribeConfig(cb, onError) {
  return onSnapshot(
    collection(db, CONFIG),
    (snap) => {
      const cfg = {};
      snap.docs.forEach((d) => (cfg[d.id] = d.data()));
      cb(cfg);
    },
    (err) => {
      console.error('subscribeConfig failed:', err);
      if (onError) onError(err);
    }
  );
}

export async function saveConfigDoc(id, data) {
  await setDoc(doc(db, CONFIG, id), data, { merge: true });
}

// ── Sessions ───────────────────────────────────────────────────────────────

// A rep's own sessions (rules enforce the repEmail match server-side too).
export function subscribeMySessions(repEmail, cb, onError) {
  const q = query(
    collection(db, SESSIONS),
    where('repEmail', '==', (repEmail || '').toLowerCase()),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error('subscribeMySessions failed:', err);
      if (onError) onError(err);
    }
  );
}

// All sessions (admin/team dashboard). Rules require the caller be a Hub admin.
export function subscribeAllSessions(cb, onError) {
  const q = query(collection(db, SESSIONS), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error('subscribeAllSessions failed:', err);
      if (onError) onError(err);
    }
  );
}

// ── Objections (admin review queue) ────────────────────────────────────────

export function subscribeObjections(cb, onError) {
  return onSnapshot(
    query(collection(db, OBJECTIONS), orderBy('createdAt', 'desc')),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error('subscribeObjections failed:', err);
      if (onError) onError(err);
    }
  );
}

export async function setObjectionStatus(id, status, note) {
  await updateDoc(doc(db, OBJECTIONS, id), {
    status,
    ...(note != null ? { note } : {}),
  });
}

// ── One-off seed (run once by a bootstrap admin on a fresh project) ─────────
// Writes starter personas + default config. Safe to re-run (merge/no-clobber
// of the archived flag on personas is the caller's concern; this uses merge).
export async function seedRoleplay(addedBy) {
  for (const p of STARTER_PERSONAS) {
    const { id, ...rest } = p;
    await savePersona(id, rest, addedBy);
  }
  await saveConfigDoc('difficulty', DEFAULT_DIFFICULTY_CONFIG);
  await saveConfigDoc('rubric', DEFAULT_RUBRIC);
  await saveConfigDoc('model', { id: DEFAULT_MODEL });
  await saveConfigDoc('limits', DEFAULT_LIMITS);
}
